import { API_BASE_URL } from '../config';

/**
 * Servicio para identificación de contactos en llamadas entrantes
 */

/**
 * Identifica un contacto por su número de teléfono
 * @param {string} phoneNumber - Número de teléfono a identificar
 * @returns {Promise<Object|null>} - Información del contacto o null si no se encuentra
 */
export const identifyContact = async (phoneNumber) => {
  try {
    console.log('🔍 Identificando contacto para número:', phoneNumber);
    
    // Obtener token de autenticación
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      console.warn('⚠️ No hay token de autenticación disponible');
      return null;
    }

    const response = await fetch(
      `${API_BASE_URL}/api/twilio/identify-contact?phoneNumber=${encodeURIComponent(phoneNumber)}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.ok) {
      const contactInfo = await response.json();
      console.log('✅ Contacto identificado:', contactInfo);
      return contactInfo;
    } else if (response.status === 404) {
      console.log('❓ Contacto no encontrado para:', phoneNumber);
      return null;
    } else {
      console.error('❌ Error al identificar contacto:', response.status, response.statusText);
      return null;
    }
  } catch (error) {
    console.error('❌ Error en identificación de contacto:', error);
    return null;
  }
};

/**
 * Formatea la información del contacto para mostrar en la UI
 * @param {Object} contactInfo - Información del contacto
 * @returns {Object} - Información formateada para la UI
 */
export const formatContactForUI = (contactInfo) => {
  if (!contactInfo) return null;

  const formatted = {
    name: contactInfo.nombre || 'Contacto conocido',
    phone: contactInfo.telefono,
    type: getContactTypeLabel(contactInfo.tipo),
    isKnown: true,
    company: contactInfo.companiaNombre,
    expediente: contactInfo.expedienteNumero,
    expedienteId: contactInfo.expedienteId, // ✨ AGREGADO: ID del expediente para navegación
    lastContact: contactInfo.ultimoContacto,
    notes: contactInfo.notas,
    
    // ✨ Nueva información
    encargado: contactInfo.encargadoExpediente,
    
    // Llamadas perdidas entrantes (ellos nos llamaron)
    tieneLlamadasPerdidas: contactInfo.tieneLlamadasPerdidas || false,
    numeroLlamadasPerdidas: contactInfo.numeroLlamadasPerdidas24h || 0,
    ultimaLlamadaPerdida: contactInfo.ultimaLlamadaPerdida,
    tiempoUltimaLlamadaPerdida: contactInfo.tiempoUltimaLlamadaPerdida,
    
    // ✨ Llamadas perdidas salientes (nosotros les llamamos)
    tieneLlamadasSalientesPerdidas: contactInfo.tieneLlamadasSalientesPerdidas || false,
    numeroLlamadasSalientesPerdidas: contactInfo.numeroLlamadasSalientesPerdidas24h || 0,
    ultimaLlamadaSalientePerdida: contactInfo.ultimaLlamadaSalientePerdida,
    tiempoUltimaLlamadaSalientePerdida: contactInfo.tiempoUltimaLlamadaSalientePerdida
  };

  // Agregar información específica según el tipo
  switch (contactInfo.tipo) {
    case 'expediente':
      formatted.type = `${contactInfo.subtipo || 'Contacto'} - Expediente`;
      formatted.details = contactInfo.expedienteNumero;
      if (contactInfo.expedienteTitulo) {
        formatted.details += ` - ${contactInfo.expedienteTitulo}`;
      }
      break;
      
    case 'compania':
      formatted.type = `${contactInfo.subtipo || 'Empresa'}`;
      formatted.details = contactInfo.companiaNombre;
      break;
      
    case 'general':
    default:
      formatted.type = contactInfo.subtipo || 'Contacto general';
      break;
  }

  return formatted;
};

/**
 * Obtiene la etiqueta del tipo de contacto
 * @param {string} tipo - Tipo de contacto
 * @returns {string} - Etiqueta legible
 */
const getContactTypeLabel = (tipo) => {
  switch (tipo) {
    case 'expediente':
      return 'Expediente';
    case 'compania':
      return 'Empresa';
    case 'general':
      return 'Contacto';
    default:
      return 'Contacto';
  }
};

/**
 * Obtiene el icono apropiado para el tipo de contacto
 * @param {string} tipo - Tipo de contacto
 * @returns {string} - Emoji del icono
 */
export const getContactTypeIcon = (tipo) => {
  switch (tipo) {
    case 'expediente':
      return '📁';
    case 'compania':
      return '🏢';
    case 'general':
    default:
      return '👤';
  }
};